import { NgClass } from '@angular/common';
import type { ElementRef, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  output,
  inject,
} from '@angular/core';

import moment from 'moment-timezone';
import { MomentModule } from 'ngx-moment';

import { AdminsAvatarsComponent } from '@core/account-ui';
import { collapse, wrapURLs } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { AppNavigatorService } from '@core/navigation-legacy';
import type { Task } from '@core/tasks-types';
import { getDefaultTimezone } from '@core/time-legacy';
import { DateComponent } from '@core/time-ui';
import {
  CheckboxComponent,
  IconComponent,
  RtStatefulComponent,
  RtTooltip,
  TruncateComponent,
} from '@core/user-interface-legacy';

type State = {
  dateClass: string | null;
  humanizedDate: string | null;
  loading: boolean;
  completed: boolean;
  animState: string;
  title: string;
};

@Component({
  selector: 'rt-task-item',
  templateUrl: './task-item.component.html',
  styleUrls: ['./task-item.component.less'],
  animations: [collapse],
  host: {
    '(click)': 'editTask()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CheckboxComponent,
    NgClass,
    RtTooltip,
    IconComponent,
    TruncateComponent,
    AdminsAvatarsComponent,
    DateComponent,
    MomentModule,
    TranslatePipe,
  ],
})
export class TaskItemComponent extends RtStatefulComponent<State> {
  private readonly i18n = inject(I18nService);
  private readonly appNavigator = inject(AppNavigatorService);
  protected readonly cdRef: ChangeDetectorRef;

  @Input() public task: Task;
  @Input() public editableCandidate: boolean = false;
  @Input() public canViewCandidatesSection: boolean = false;
  @Input() public adminTimezone: string = getDefaultTimezone();
  @ViewChild('taskTitle', { static: true })
  public taskTitle: ElementRef;
  public dueDate: moment.Moment;
  public dueDateTooltipFormat: string = 'lll';

  public openTaskEditDialog = output<Task>();
  public taskToggle = output<{
    task: Task;
    completed: boolean;
  }>();

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      dateClass: null,
      humanizedDate: null,
      loading: false,
      completed: false,
      animState: 'normal',
      title: '',
    });
  }

  public get isEditable(): boolean {
    return this.canViewCandidatesSection;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.task && changes.task.currentValue && changes.task.currentValue.dueDate) {
      this.humanizeDate();
    }

    if (changes.task && changes.task.currentValue) {
      this.setState({
        ...this.state,
        completed: changes.task.currentValue.completed,
        loading: false,
        title: wrapURLs(this.task.title),
      });

      this.assignStopPropagationToElement();
    }
  }

  public toggle(): void {
    this.sendRequest(!this.state.completed);
  }

  public editTask(): void {
    this.openTaskEditDialog.emit(this.task);
  }

  public goToCandidate(event: any, id: number): void {
    if (id) this.appNavigator.openCandidateDialog(id);
    event.stopPropagation();
  }

  private assignStopPropagationToElement() {
    this.taskTitle.nativeElement.querySelectorAll('a').forEach((node: HTMLElement) => {
      node.addEventListener('click', $event => {
        $event.stopPropagation();
      });
    });
  }

  private sendRequest(isCompleted: boolean): void {
    this.setState({
      ...this.state,
      loading: true,
    });
    this.taskToggle.emit({ task: this.task, completed: isCompleted });
  }

  private humanizeDate(): void {
    this.dueDate = moment.tz(this.task.dueDate, this.adminTimezone || moment.tz.guess());
    const dateNow = moment.utc().tz(this.adminTimezone || moment.tz.guess()) || moment();
    const diff = this.dueDate.diff(dateNow, 'days', true);
    let dateClass;

    if (Math.abs(diff) < 1) {
      if (this.dueDate.date() === dateNow.date()) {
        dateClass = 'today';
      } else if (diff < 0) {
        dateClass = 'yesterday';
      } else if (diff > 0) {
        dateClass = 'tomorrow';
      }
    } else if (Math.abs(diff) < 2) {
      if (diff < 0 && this.dueDate.date() === dateNow.subtract(1, 'day').date()) {
        dateClass = 'yesterday';
      } else if (diff > 0 && this.dueDate.date() === dateNow.add(1, 'day').date()) {
        dateClass = 'tomorrow';
      }
    }

    const humanizedDate = dateClass
      ? this.i18n.translate(`app.common.${dateClass}`)
      : this.dueDate.format('DD MMM YYYY');

    this.setState({
      ...this.state,
      dateClass: <string>dateClass,
      humanizedDate,
    });
  }
}
